import {getApp} from 'firebase/app';
import {
  User,
  createUserWithEmailAndPassword,
  getAuth,
  sendEmailVerification,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
} from 'firebase/auth';
import React from 'react';
import {QueryClient} from 'react-query';

import {ApiOrganization, ApiUserProfile} from 'src/lib/api/auth';
import {LOGIN_APP_URL, setWindowLocationHref, startSignOutHandoff} from 'src/lib/routeUtils';

import {useCurrentOrgIdPrefix} from './useCurrentOrgIdPrefix';

export type LoggedInAuth = {
  status: 'logged in';
  userProfile: ApiUserProfile | null;
  /**
   * All organizations associated with the user's profile.
   */
  profileOrganizations: ApiOrganization[];
  /**
   * The “current” organization that the user is operating the app under. May
   * not be the same as the profile’s organization, if the user is a member of a
   * parent org and has switched into this one.
   *
   * NOTE: in some situations (e.g. when using back/forward buttons around
   * org switch behavior), this may get out of date.
   * (See examples in discussion on PR#826.)
   * We introduced a new hook, useCurrentOrganization(), which
   * determines the current org based on the org id prefix in the URL (a
   * better source of truth); we recommend defaulting to using that instead.
   *
   * (TODO: investigate the state issues around this more, and/or fully
   * replace this with useCurrentOrganization().)
   */
  currentOrganization: ApiOrganization | null;
  /**
   * Calling this remounts the app through AuthProvider (auth cycles through
   * undefined). Don't pair it with a navigate() in the same handler: pages
   * with query-param write-back effects remount mid-transition and cancel the
   * pending navigation. See OrgSwitch.test.tsx.
   */
  setCurrentOrganization: (org: ApiOrganization) => void;

  firebaseUid: string;
  firebaseEmail: string;
  firebaseToken: string;
  firebaseIsEmailVerified: boolean;
  photoUrl: string | null | undefined;
  /** Time in seconds since the user signed up */
  signupTimeSecs: number;
  signOut: () => Promise<void> | void;
  sendVerificationEmail: () => Promise<void> | void;
};

export type LoggedOutAuth = {
  status: 'logged out';
  signIn: (email: string, password: string) => Promise<void>;
  makeUser: (args: MakeUserArgs) => Promise<unknown>;
  resetPassword: (email: string) => Promise<void>;
};

export type Auth = LoggedInAuth | LoggedOutAuth;
export const AuthContext = React.createContext<Auth | undefined>(undefined);

type MakeUserArgs = {
  email: string;
  password: string;
};

async function makeUser(args: MakeUserArgs) {
  const app = getApp();
  const auth = getAuth(app);

  const {email, password} = args;

  const firebaseUser = (await createUserWithEmailAndPassword(auth, email, password)).user;

  if (!firebaseUser) {
    throw new Error('User creation was unsuccessful, but did not throw an error');
  }

  return firebaseUser;
}

export function makeLoggedOutAuth(): LoggedOutAuth {
  const app = getApp();
  const auth = getAuth(app);

  return {
    status: 'logged out',
    // In the old code, signIn and resetPassword were behind an
    // exponential backoff retry that retried once in case of failure. Is
    // that still necessary?
    signIn: async (email: string, password: string) => {
      await signInWithEmailAndPassword(auth, email, password);
    },
    makeUser,
    resetPassword: async (email: string) => {
      await sendPasswordResetEmail(auth, email);
    },
  };
}

export function makeLoggedInAuth(
  queryClient: QueryClient,
  firebaseUser: User,
  idToken: string,
  userProfile: ApiUserProfile | null,
  currentOrganization: ApiOrganization | null,
  profileOrganizations: ApiOrganization[],
  setCurrentOrganization: LoggedInAuth['setCurrentOrganization'],
  setLoggingOut: (isLoggingOut: boolean) => void
): LoggedInAuth {
  const app = getApp();
  const auth = getAuth(app);
  return {
    status: 'logged in',
    firebaseUid: firebaseUser.uid,
    firebaseEmail: firebaseUser.email!,
    firebaseToken: idToken,
    firebaseIsEmailVerified: !!firebaseUser.emailVerified,
    signupTimeSecs: firebaseUser.metadata.creationTime
      ? Date.parse(firebaseUser.metadata.creationTime) / 1000
      : 0,
    photoUrl: auth.currentUser?.photoURL,
    userProfile,
    currentOrganization,
    profileOrganizations,
    setCurrentOrganization,
    signOut: async () => {
      queryClient.clear();
      setLoggingOut(true);
      startSignOutHandoff();
      await auth.signOut();
      // The login app's /signOut is what ends the root session, and revokes the ones the
      // sibling apps hold. Clearing this origin alone would leave the user signed in there.
      setWindowLocationHref(`${LOGIN_APP_URL}/signOut`);
    },
    sendVerificationEmail: () => sendEmailVerification(firebaseUser),
  };
}

export default function useAuth() {
  const auth = React.useContext(AuthContext);

  // AuthProvider enforces a contract that it will never render its children
  // unless auth is defined, so this is a programmer error rather than an odd
  // state.
  if (auth === undefined) {
    throw new Error('Auth consumer used outside of AuthProvider');
  }

  return auth;
}

/** Returns null (rather than requiring an AuthProvider) when logged out or unmounted. */
export function useOptionalUserProfile(): ApiUserProfile | null {
  const auth = React.useContext(AuthContext);
  return auth?.status === 'logged in' ? auth.userProfile : null;
}

export function useCurrentOrganization(): ApiOrganization | null {
  /**
   * Helper hook to get the "current" organization (aka the org that the user is
   * operating the app under -- this may not be the same as their profile organization,
   * if the user is a member of a parent org and has switched into a child org.)
   *
   * This hook prioritizes matching the URL org prefix over using auth state.
   * This ensures that the result is always up to date when switching orgs
   * or using back/forward buttons.
   * (See comment on LoggedInAuth.currentOrganization for more context.)
   */
  const auth = useAuth();
  const currentOrgIdPrefix = useCurrentOrgIdPrefix(false);

  return React.useMemo(() => {
    if (auth.status !== 'logged in') return null;
    // Prioritize the org that matches the URL org prefix as the source of truth
    // (over the auth.currentOrganization state) because it always immediately updates
    // when switching orgs or using back/forward buttons.
    if (currentOrgIdPrefix && auth.profileOrganizations.length) {
      const orgWithIdPrefix = getOrgWithIdPrefix(currentOrgIdPrefix, auth.profileOrganizations);
      if (orgWithIdPrefix) {
        return orgWithIdPrefix;
      }
    }
    // if we don't have a currentOrgIdPrefix for some odd reason, use the current Org (may be null)
    return auth.currentOrganization;
  }, [auth, currentOrgIdPrefix]);
}

export function getOrgWithIdPrefix(
  orgIdPrefix: string,
  profileOrganizations: ApiOrganization[]
): ApiOrganization | null {
  for (const profileOrganization of profileOrganizations) {
    // If the prefix matches a profile org itself, use that
    if (profileOrganization.id.startsWith(orgIdPrefix)) {
      return profileOrganization;
    }
    // else search through the child orgs to figure out where we are
    const matchingChildOrg = profileOrganization.childOrganizations?.find((org) =>
      org.id.startsWith(orgIdPrefix)
    );
    if (matchingChildOrg) {
      return matchingChildOrg;
    }
  }

  return null;
}
